
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  feedback: true,
  toc: true,
  layout: 'guide',
  description: 'Risk checks'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Risk checks' subtitle='Understand what each Risk Check means and factors that influence results' mdxType="Header" />
    <p>{`This page outlines the various checks performed in the Risk Check category to help you understand the results and what Identity Verification evaluates during each check.`}</p>
    <p>{`For each session's risk check results, the Identity Verification Dashboard will show the largest factors contributing to the risk.`}</p>
    <h4 {...{
      "id": "email-risk"
    }}>{`Email Risk`}</h4>
    <Callout mdxType="Callout">
      <p>{`Identity Verification does not collect an email address during the Link flow. Email risk will only be assessed if you collect the end user's email address and provide it to Plaid via `}<inlineCode parentName="p">{`/identity_verification/create`}</inlineCode>{` or `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Only verified email addresses should be sent for email risk assessment.`}</p>
    </Callout>
    <p>{`Attributes that increase email risk include:`}</p>
    <ul>
      <li parentName="ul">{`Email provided via a disposable email service, such as Mailinator. This is a strong fraud signal.`}</li>
      <li parentName="ul">{`Email associated with a recently-registered (<3 months old) domain.`}</li>
      <li parentName="ul">{`Email domain not deliverable. We do a live check on the domain to see if it is configured to receive email. Failing this test means the email is fake, which is a strong fraud signal.`}</li>
      <li parentName="ul">{`Email not present on breach lists or only present on newer breaches (presence on older lists indicates an older, established, actively-used email address)`}</li>
      <li parentName="ul">{`Email registered with few or no popular services, such as social media platforms. We check over 90 services for connections. In the Dashboard, all of the services that the email is detected as associated with will be displayed.`}</li>
    </ul>
    <h4 {...{
      "id": "device-risk"
    }}>{`Device risk`}</h4>
    <p>{`Plaid looks at multiple attributes of the end user's device. Attributes that can increase device risk include:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Risk factor`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Contribution to device risk`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Proxy usage`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moderate`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`VPN usage`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moderate`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Tor usage`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`High`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`IP address matching known-malicious IP lists`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`High`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Datacenter IP address (correlated with abuse)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`High`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`IP address geolocation mismatch vs KYC data or device time zone`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moderate`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Suspicious open ports`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moderate`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Incognito sessions`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moderate`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Cookies disabled`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moderate`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Large number of sessions / devices (indicative of fraud ring / account farm)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`High`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "identity-verification-network-risk"
    }}>{`Identity Verification Network Risk`}</h4>
    <Callout mdxType="Callout">
      <p>{`In Sandbox, when testing, make sure to lower risk thresholds for Network Risk, as normal testing behavior can trigger the Identity Verification Network Risk Check.`}</p>
    </Callout>
    <p>{`Plaid creates a device fingerprint based on device/session attributes, including IP address, location, browser plugins, OS settings, WebGL parameters, user agent, TCP settings, cookies, screen resolution, battery usage, and device memory. The fingerprint allows Plaid to identify when the same device is used for multiple Identity Verification sessions.`}</p>
    <p>{`Plaid tracks the velocity of sessions per device across all customers. If a device is onboarding with many different Plaid clients in a short timeframe, this suggests coordinated fraud attempts. The dashboard will show counts for sessions in the last 24 hours, 7 days, 3 months, and all-time.`}</p>
    <p>{`If a device is seen creating multiple accounts in a short period (e.g. several signups in one day), this is also flagged as high risk, since it is often indicative of fraud rings or account farming.`}</p>
    <h4 {...{
      "id": "phone-risk"
    }}>{`Phone risk`}</h4>
    <p>{`Phone risk checks for throwaway / burner phone numbers. Plaid checks for phone number registration with over 90 services. More links indicates lower risk.`}</p>
    <h4 {...{
      "id": "stolen-identity-risk-and-synthetic-identity-risk"
    }}>{`Stolen identity risk and synthetic identity risk`}</h4>
    <Callout mdxType="Callout">
      <p>{`Stolen identity risk and synthetic identity risk will be assessed only for US end users, and only if a full 9-digit Social Security Number is available. `}</p>
    </Callout>
    <p>{`Note that while stolen and synthetic identity risk are displayed in the Dashboard as percentages, the numbers do not indicate a percentage likelihood that the identity is stolen or synthetic. Instead, they merely represent that the score is normalized to be in the 0-100 range. A score of 70% or higher for synthetic identity, or a score of 90% or higher for stolen identity, indicates high risk.`}</p>
    <p>{`Risk factors for stolen identity include:`}</p>
    <ul>
      <li parentName="ul">{`Multiple different identities associated with the same phone number, email, or SSN`}</li>
      <li parentName="ul">{`PII associated with deceased individual (e.g. SSN found in Death Master File)`}</li>
    </ul>
    <p>{`Risk factors for synthetic identity include:`}</p>
    <ul>
      <li parentName="ul">{`Multiple different identities associated with the same phone number, email, or SSN`}</li>
      <li parentName="ul">{`PII associated with deceased individual (e.g. SSN found in Death Master File)`}</li>
      <li parentName="ul">{`SSN inconsistent with date of birth or address history`}</li>
      <li parentName="ul">{`Brief or no established usage of email address, phone number, or SSN (see `}<a parentName="li" {...{
          "href": "#phone-risk"
        }}>{`phone risk`}</a>{` and `}<a parentName="li" {...{
          "href": "#email-risk"
        }}>{`email risk`}</a>{` for more details on how usage history for these values is established)`}</li>
    </ul>
    <h4 {...{
      "id": "facial-duplicate-risk"
    }}>{`Facial duplicate risk`}</h4>
    <Callout mdxType="Callout">
      <p>{`Facial duplicate risk will be assessed only if either Selfie Checks or Documentary Verification is enabled.`}</p>
    </Callout>
    <p>{`Facial duplicate risk detects users whose facial biometrics (from selfies or ID document portraits) match those from previous verification sessions. This is designed to catch repeat fraud attempts, synthetic identity attacks, and incentive abuse.`}</p>
    <h4 {...{
      "id": "behavior-risk"
    }}>{`Behavior risk`}</h4>
    <p>{`Behavior risk analyzes a user's behavior to determine whether it appears human and genuine. Factors analyzed include:`}</p>
    <ul>
      <li parentName="ul">{`How fast a user types in their PII`}</li>
      <li parentName="ul">{`How accurately a user enters their PII`}</li>
      <li parentName="ul">{`Whether the data is copied and pasted`}</li>
      <li parentName="ul">{`The field order in which a user inputs data`}</li>
      <li parentName="ul">{`Mouse movement and scrolling patterns`}</li>
      <li parentName="ul">{`The use of autofill`}</li>
      <li parentName="ul">{`The frequency and device variety of entering the same or similar PII (see also `}<a parentName="li" {...{
          "href": "#identity-verification-network-risk"
        }}>{`network risk`}</a>{`).`}</li>
    </ul>
    <p>{`These are categorized into three risk buckets.`}</p>
    <p><strong parentName="p">{`Behavior:`}</strong>{` "Risky" means the user's interaction with the form is statistically anomalous compared to legitimate users, but does not necessarily show signs of being a bot or a fraud ring. For example, users typically enter their own PII fluently, so entering PII with hesitation or multiple corrections may indicate stolen identity.`}</p>
    <p><strong parentName="p">{`Fraud Ring:`}</strong>{` "Yes" means the session matches known fraud ring patterns. For example, repeatedly entering and correcting similar PII on a single device or across multiple devices simultaneously can indicate a fraud ring attempting a synthetic identity attack.`}</p>
    <p><strong parentName="p">{`Bot:`}</strong>{` "Yes" means the session exhibited patterns associated with non-human, non-autofill, automated data entry.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "email-risk",
        level: 4,
        title: "Email Risk",
      },
{
        id: "device-risk",
        level: 4,
        title: "Device risk",
      },
{
        id: "identity-verification-network-risk",
        level: 4,
        title: "Identity Verification Network Risk",
      },
{
        id: "phone-risk",
        level: 4,
        title: "Phone risk",
      },
{
        id: "stolen-identity-risk-and-synthetic-identity-risk",
        level: 4,
        title: "Stolen identity risk and synthetic identity risk",
      },
{
        id: "facial-duplicate-risk",
        level: 4,
        title: "Facial duplicate risk",
      },
{
        id: "behavior-risk",
        level: 4,
        title: "Behavior risk",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
